// Identity store.
//
// Svelte 5 runes hold the authenticated user's email, display name, and
// role set. The interface bootstraps the store on mount by calling
// GET /api/identity through the proxy Worker; the proxy reads
// data/access-control.json and returns the resolved role set.
//
// In dev, setDevIdentity (api.ts) places the chosen identity into
// sessionStorage and the bootstrap re-issues the request with the
// X-Dev-Identity header set.

import { api, ApiError, getDevIdentity, setDevIdentity } from './api';
import type { Affordances } from './roles';
import { affordancesFor } from './roles';
import type { IdentityResult } from './types';

export interface IdentityState {
  loading: boolean;
  email: string | null;
  display_name: string | null;
  roles: readonly string[];
  affordances: Affordances;
  error: string | null;
  not_provisioned: boolean;
}

const initialAffordances = affordancesFor([]);

const identityState = $state<IdentityState>({
  loading: true,
  email: null,
  display_name: null,
  roles: [],
  affordances: initialAffordances,
  error: null,
  not_provisioned: false
});

export const identity = identityState;

function applyResult(result: IdentityResult): void {
  if (result.email === null) {
    identityState.loading = false;
    identityState.email = null;
    identityState.display_name = null;
    identityState.roles = [];
    identityState.affordances = initialAffordances;
    identityState.not_provisioned = result.reason === 'no_access_control_entry';
    identityState.error = null;
    return;
  }
  identityState.loading = false;
  identityState.email = result.email;
  identityState.display_name = result.display_name;
  identityState.roles = result.roles;
  identityState.affordances = affordancesFor(result.roles);
  identityState.not_provisioned = false;
  identityState.error = null;
}

export async function bootstrapIdentity(): Promise<void> {
  identityState.loading = true;
  identityState.error = null;
  try {
    const result = await api.identity();
    applyResult(result);
  } catch (err) {
    identityState.loading = false;
    if (err instanceof ApiError) {
      if (err.status === 403 && err.code === 'cloudflare_access_header_missing') {
        identityState.error = 'cloudflare_access_header_missing';
        return;
      }
      if (err.status === 403) {
        identityState.error = err.message;
        return;
      }
    }
    identityState.error = err instanceof Error ? err.message : 'identity_unknown_error';
  }
}

// Listener registry for identity-change events. Subscribers register
// at app boot (main.ts) to reset module-level state that should not
// survive an identity switch — for instance, the staged-edits store
// holds in-memory edits keyed by the previous identity's session.
// An event bus rather than a direct cross-module call avoids a
// circular import between this file and staged-edits.svelte.ts.
const identityChangeListeners: Array<() => void> = [];

export function onIdentityChange(listener: () => void): void {
  identityChangeListeners.push(listener);
}

function notifyIdentityChange(): void {
  for (const fn of identityChangeListeners) {
    try {
      fn();
    } catch {
      // A misbehaving listener must not block the identity switch
      // itself; the swallow here is intentional. The proxy Worker's
      // role check is the authoritative line of defence regardless.
    }
  }
}

export async function switchDevIdentity(email: string | null): Promise<void> {
  setDevIdentity(email);
  notifyIdentityChange();
  await bootstrapIdentity();
}

export function currentDevIdentity(): string | null {
  return getDevIdentity();
}
